import { Link } from "react-router-dom";
import { Phone, Mail, Globe, Send } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { buildWhatsappUrl, buildTelegramUrl, WhatsAppIcon } from "@/components/SocialFloat";
import logo from "@/assets/bgrmatka-logo.png";

export function Footer() {
  const { settings } = useSettings();
  const waUrl = buildWhatsappUrl(settings.whatsapp);
  const tgUrl = buildTelegramUrl(settings.telegram);
  return (
    <footer className="mt-10 border-t border-[color:var(--border-gold)] bg-[color:var(--bg-secondary)]">
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="grid gap-6 sm:grid-cols-3">
          <div>
            <div className="flex items-center gap-2">
              <img src={logo} alt={settings.business_name} className="h-9 w-9 object-contain" />
              <span className="font-display text-lg font-extrabold">{settings.business_name}</span>
            </div>
            <p className="mt-3 text-sm text-[color:var(--text-muted)]">
              {settings.tagline ? `${settings.tagline}. ` : ""}Fast, accurate and mobile-first result display for {settings.domain}.
            </p>
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-[color:var(--accent-gold)]">Quick Links</h4>
            <ul className="grid grid-cols-2 gap-1.5 text-sm text-[color:var(--text-secondary)]">
              {[
                { to: "/", label: "Home" },
                { to: "/live", label: "Live" },
                { to: "/results", label: "Results" },
                { to: "/charts", label: "Charts" },
                { to: "/records", label: "Records" },
                { to: "/contact", label: "Contact" },
              ].map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="hover:text-[color:var(--accent-gold-bright)]">{i.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-[color:var(--accent-gold)]">Contact</h4>
            <ul className="space-y-2 text-sm text-[color:var(--text-secondary)]">
              {settings.mobile && (
                <li><a href={`tel:${settings.mobile}`} className="inline-flex items-center gap-2 hover:text-[color:var(--accent-gold-bright)]"><Phone className="h-4 w-4" /> {settings.mobile}</a></li>
              )}
              <li><a href={`mailto:${settings.email}`} className="inline-flex items-center gap-2 hover:text-[color:var(--accent-gold-bright)]"><Mail className="h-4 w-4" /> {settings.email}</a></li>
              <li><span className="inline-flex items-center gap-2"><Globe className="h-4 w-4" /> {settings.domain}</span></li>
              {waUrl && (
                <li><a href={waUrl} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="inline-flex items-center gap-2 hover:text-[color:var(--accent-gold-bright)]"><WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> WhatsApp Channel</a></li>
              )}
              {tgUrl && (
                <li><a href={tgUrl} target="_blank" rel="noreferrer" data-testid="footer-telegram" className="inline-flex items-center gap-2 hover:text-[color:var(--accent-gold-bright)]"><Send className="h-4 w-4 text-[#229ED9]" /> Telegram Channel</a></li>
              )}
            </ul>
          </div>
        </div>

        <div data-testid="footer-disclaimer" className="mt-8 rounded-xl border-2 border-[color:var(--border-gold)] bg-[color:var(--bg-card)] p-4 text-center">
          <p className="font-display text-sm font-extrabold uppercase tracking-widest text-[color:var(--accent-crimson)]">-: Disclaimer :-</p>
          <p className="mt-2 text-xs leading-relaxed text-[color:var(--text-secondary)] sm:text-sm">
            This website is intended for informational purposes only. All information provided on {settings.business_name} is based on publicly
            available data, numerical analysis, and general informational content. We are not associated with any illegal Satta Matka or
            gambling activity. Users are advised to check their local laws before accessing such information. We do not promote or encourage
            any form of betting or gambling. By using this website, you agree that you are accessing the information at your own risk. The site
            owners are not responsible for any loss, damage, or legal issues. Copying or reproducing any content from this website without
            permission is strictly prohibited.
          </p>
          <p className="mt-3 text-xs font-extrabold uppercase tracking-wider text-[color:var(--accent-gold)]">Powered by {settings.domain}</p>
        </div>

        <div className="mt-5 border-t border-white/10 pt-4 text-center text-xs text-[color:var(--text-muted)]">
          <p data-testid="footer-copyright">Copyright © 2024-{new Date().getFullYear()} {settings.business_name}. All rights reserved. All information provided is for informational purposes only.</p>
          <nav className="mt-2 flex flex-wrap justify-center gap-x-1 gap-y-1 font-semibold text-[color:var(--text-secondary)]">
            {[["Terms", "/info/terms"], ["Contact Us", "/contact"], ["Privacy Policy", "/info/privacy"], ["Cookie Policy", "/info/cookie"], ["About Us", "/info/about"], ["Blog", "/info/blog"]].map(([label, to], i, arr) => (
              <span key={to}>
                <Link to={to} data-testid={`footer-link-${to.split("/").pop()}`} className="hover:text-[color:var(--accent-gold-bright)]">{label}</Link>
                {i < arr.length - 1 && <span className="mx-1 opacity-50">|</span>}
              </span>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
}
